/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * "Show audit rows" (docs/CONTRACT-fleet-governance.md sections 9.3 and 12.3): the audit rows behind a Trends number, from
 * GET /v1/admin/governance/rows with the number's event or event prefix, its app and tool, and its day or the range. The
 * ledger answers while the rows are in its window; the governance copy answers once the window has rolled past the day
 * (the drawer shows the service's note), and past the copy's retention the rows have rolled off. The rows carry actors,
 * as the Audit log shows them to the same two roles. "Load more" pages back by `next.beforeSeq`; copy rows offer the
 * copy's own export for the same filters.
 */
import { useState } from 'react';
import { Download, ScrollText } from 'lucide-react';
import { addDays, daysOf, drillQuery, rowsExportHref, trendsApi, trendsFailure, type DrillRequest, type GovAuditRow } from '../../lib/trends';
import { Button, Callout, Drawer, EmptyState, LinkButton, Segmented, Select, Skeleton, Table, Tag, type Column } from '../../ui';
import { count, dateTime } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { LoadError } from '../fleet/parts';

/** Mounted afresh for every request (its choice, day and pages start over). */
export function AuditRowsDrawer({ request, onClose }: { request: DrillRequest | null; onClose: () => void }) {
  if (!request) return null;
  return <RowsDrawer key={JSON.stringify(request)} request={request} onClose={onClose} />;
}

const columns: Array<Column<GovAuditRow>> = [
  { key: 'time', header: 'Time', muted: true, cell: (row) => dateTime(row.ts) },
  { key: 'actor', header: 'Actor', muted: true, cell: (row) => row.actor.email ?? row.actor.id },
  { key: 'app', header: 'App', hideOnPhone: true, cell: (row) => row.app ?? '—' },
  { key: 'event', header: 'Event', cell: (row) => <Tag>{row.event}</Tag> },
  { key: 'tool', header: 'Tool', mono: true, hideOnPhone: true, cell: (row) => row.tool ?? '—' },
  { key: 'risk', header: 'Risk', hideOnPhone: true, cell: (row) => row.riskTier ?? '—' },
  { key: 'outcome', header: 'Outcome', muted: true, cell: (row) => row.outcome ?? '—' },
  { key: 'detail', header: 'Detail', muted: true, cell: (row) => (row.detail ? <span className="ui-clip" title={row.detail}>{row.detail}</span> : '—') },
];

function RowsDrawer({ request, onClose }: { request: DrillRequest; onClose: () => void }) {
  const [choiceIndex, setChoiceIndex] = useState(0);
  const [day, setDay] = useState<string | null>(request.day ?? null);
  const [more, setMore] = useState<{ key: string; items: GovAuditRow[]; next: { beforeSeq: number } | null } | null>(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const choice = request.choices[choiceIndex] ?? request.choices[0];
  const built = choice ? drillQuery(request, choice, day) : null;
  const key = built ? JSON.stringify(built.query) : '';
  const { load, reload, reloading } = useLoad(() => (built ? trendsApi.rows(built.query) : Promise.resolve(null)), key);
  const first = load.state === 'ready' ? load.data : null;
  const extra = more && more.key === key ? more : null;
  const items = first ? [...first.items, ...(extra?.items ?? [])] : [];
  const next = extra ? extra.next : first?.next ?? null;
  const fromCopy = first?.source === 'copy' || first?.source === 'ledger+copy';

  const loadMore = async () => {
    if (!next || !choice) return;
    setMoreLoading(true);
    setMoreError(null);
    try {
      const page = await trendsApi.rows(drillQuery(request, choice, day, next.beforeSeq).query);
      setMore((current) => ({ key, items: [...(current && current.key === key ? current.items : []), ...page.items], next: page.next }));
    } catch (error) {
      setMoreError(error);
    } finally {
      setMoreLoading(false);
    }
  };

  const scope = [choice?.label, request.app ?? 'all apps', request.tool].filter(Boolean).join(' · ');
  const span = built ? `${built.from} to ${addDays(built.to, -1)}` : '';

  return (
    <Drawer
      open
      onClose={onClose}
      title={request.title}
      description={built ? `${scope} · ${span}` : scope}
      className="ui-drawer--wide"
      footer={fromCopy && built ? (
        <LinkButton
          size="sm"
          icon={Download}
          href={rowsExportHref({ from: built.from, to: built.to, event: choice?.event, app: request.app })}
          download
          title={choice?.event ? 'The governance copy\'s rows for this event, app and window, as NDJSON; the export is audited.' : 'The governance copy\'s rows for this app and window (every copied event: the export takes an exact event, not a prefix), as NDJSON; the export is audited.'}
        >
          Export the governance copy's rows
        </LinkButton>
      ) : undefined}
    >
      {!choice ? (
        <EmptyState compact icon={ScrollText} title="No audit rows behind this number">It is counted from the turn traces, which write no audit rows.</EmptyState>
      ) : (
        <div className="ui-form">
          <span className="ui-toolbar">
            {request.choices.length > 1 ? (
              <Segmented label="Which rows" options={request.choices.map((item, index) => ({ value: String(index), label: item.label }))} value={String(choiceIndex)} onChange={(value) => setChoiceIndex(Number(value))} />
            ) : null}
            <Select size="sm" className="ui-w-auto" aria-label="Day" value={day ?? ''} onChange={(event) => setDay(event.target.value || null)}>
              <option value="">{`Whole range (${request.range.from} to ${request.range.to})`}</option>
              {daysOf(request.range).reverse().map((value) => <option key={value} value={value}>{value}</option>)}
            </Select>
          </span>
          {built?.clamped ? <Callout tone="info">This range is longer than 31 days, so the rows of its last 31 days are shown. Pick a day to see an earlier one.</Callout> : null}
          {first?.note ? <Callout tone={first.source === 'rolled-off' ? 'warn' : 'info'}>{first.note}</Callout> : null}
          {load.state === 'loading' ? <div aria-busy="true"><Skeleton size="lg" /><Skeleton /><Skeleton /></div> : null}
          {load.state === 'failed' ? <LoadError title="The audit rows could not be loaded" error={load.error} words={(error) => trendsFailure(error)} onRetry={reload} retrying={reloading} /> : null}
          {first ? (
            <>
              <div className="ui-table-box">
                <Table
                  stacked
                  label="Audit rows"
                  columns={columns}
                  rows={items}
                  rowKey={(row) => String(row.seq)}
                  stickyHeader={false}
                  empty={first.source === 'rolled-off' ? <p className="ui-para ui-quiet">No rows: this window is older than the audit log and the governance copy keep.</p> : <EmptyState compact icon={ScrollText} title="No audit rows in this window">Try the whole range, another day or another choice above.</EmptyState>}
                />
              </div>
              <span className="ui-toolbar">
                <span className="ui-quiet">{`${count(items.length)} row${items.length === 1 ? '' : 's'} shown, newest first`}</span>
                {next ? <Button size="sm" busy={moreLoading} onClick={() => void loadMore()}>Load more</Button> : null}
              </span>
              {moreError ? <LoadError title="The next rows could not be loaded" error={moreError} words={(error) => trendsFailure(error)} /> : null}
            </>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}
